import { useRef } from "react";

import { noop } from "lodash";

import { Tab, TabList } from "@similarweb/ui-components";

import useIntersectionObserver from "../../hooks/useIntersectionObserver";

import { NAVIGATION_TOP_MARGIN, StyledTabs, StyledTabsWrapper } from "./styles";
import type { StickyTabItemConfig } from "./types";

interface StickyTabsWithoutCollapseProps {
    activeTab: string;
    viewportElement: HTMLDivElement | null;
    tabsConfig: StickyTabItemConfig[];
    tabRefs: React.MutableRefObject<HTMLDivElement | null>[];
    onTabClick?: (tabName: string) => void;
}

// TODO move to common-components: https://similarweb-rnd.atlassian.net/browse/SI-7584
export const StickyTabsWithoutCollapse = ({
    activeTab,
    viewportElement,
    tabsConfig,
    tabRefs,
    onTabClick = noop,
}: StickyTabsWithoutCollapseProps) => {
    const tabsRef = useRef<HTMLDivElement>(null);

    const handleIntersection = (entry: IntersectionObserverEntry) => {
        entry.target.classList.toggle("isSticky", !entry.isIntersecting);
    };

    useIntersectionObserver({
        onIntersection: handleIntersection,
        observingElement: tabsRef.current,
        options: { threshold: [1] },
    });

    const handleScrollToSection = (tabIndex: number) => {
        const stickySectionRef = tabRefs[tabIndex]?.current;
        if (!stickySectionRef) {return;}
        viewportElement?.scrollTo({
            top: stickySectionRef.offsetTop - NAVIGATION_TOP_MARGIN,
            behavior: "smooth",
        });
    };

    const handleTabClick = (tabIndex: number) => {
        handleScrollToSection(tabIndex);
        onTabClick(tabsConfig[tabIndex].id);
    };

    const activeTabIndex = tabsConfig.findIndex((tab) => tab.id === activeTab);

    return (
        <StyledTabsWrapper
            ref={tabsRef}
            data-automation="sticky-tabs-nav"
        >
            <StyledTabs>
                <TabList>
                    {tabsConfig.map((tab, index) => (
                        <Tab
                            key={tab.id}
                            onClick={() => handleTabClick(index)}
                            className={index === activeTabIndex ? "si-sticky-tab-selected" : "si-sticky-tab"}
                            data-automation="sticky-tab"
                            data-automation-tab-name={tab.id}
                        >
                            {tab.title}
                        </Tab>
                    ))}
                </TabList>
            </StyledTabs>
        </StyledTabsWrapper>
    );
};
